import { dateToIsoDay, isoDayToInputDate } from "@/utils/dates";

// The create-session wizard draft is per tab, so concurrent wizards in other
// tabs can't overwrite or clear it. sessionStorage survives reloads and Back.
const storage = sessionStorage;

export function getFlexBoards() {
  return read("flexBoards", []);
}

export function getSwimlanes() {
  return read("swimlanes", {});
}

export function getSwimlanesConfig() {
  return read("swimlanesConfig", {});
}

export function setSwimlanesConfig(swimlanesConfig) {
  return set("swimlanesConfig", swimlanesConfig);
}

export function setSwimlanes(swimlanes) {
  return set("swimlanes", swimlanes);
}

export function clearSwimlanes() {
  return storage.removeItem("swimlanes");
}

export function setFlexBoards(boards) {
  return set("flexBoards", boards);
}

export function getLinks() {
  return read("links", []);
}

export function setLinks(links) {
  return set("links", links);
}

export function getStickers() {
  return read("stickers");
}

export function setStickers(stickers) {
  set("stickers", stickers);
}

export function getSettings() {
  return read("settings", {});
}

export function setSettings(settings) {
  return set("settings", settings);
}

/** Copies the draft's settings over the given defaults; keys the draft never set keep theirs. */
export function restoreSettings(settings) {
  const draft = getSettings();
  for (const key in settings) {
    if (draft[key] !== undefined && draft[key] !== null) {
      settings[key] = draft[key];
    }
  }
}

export function getGeneralInformation() {
  const info = read("generalInformation");
  if (!info) return info;
  return {
    ...info,
    planningStartDate: isoDayToInputDate(info.planningStartDate),
    planningEndDate: isoDayToInputDate(info.planningEndDate),
  };
}

export function setGeneralInformation(generalInformation) {
  set("generalInformation", {
    ...generalInformation,
    planningStartDate: dateToIsoDay(generalInformation.planningStartDate),
    planningEndDate: dateToIsoDay(generalInformation.planningEndDate),
  });
}

export function getSelectedTeams() {
  return read("selectedTeams", []);
}

export function setSelectedTeams(teams) {
  set("selectedTeams", teams);
}

export function getSelectedParticipatingTeams() {
  return read("selectedParticipatingTeams");
}

export function setSelectedParticipatingTeams(selectedParticipatingTeams) {
  if (!selectedParticipatingTeams) {
    return;
  }
  set("selectedParticipatingTeams", selectedParticipatingTeams);
}

export function getTeamStickies(fallback) {
  return read("teamStickies", fallback);
}

export function setTeamStickies(stickies) {
  set("teamStickies", stickies);
}

export function getRiskStickies(fallback) {
  return read("riskStickies", fallback);
}

export function setRiskStickies(stickies) {
  set("riskStickies", stickies);
}

export function getProgramStickies(fallback) {
  return read("programStickies", fallback);
}

export function setProgramStickies(stickies) {
  set("programStickies", stickies);
}

export function getSolutionPlanningStickies(fallback) {
  return read("solutionStickies", fallback);
}

export function setSolutionPlanningStickies(stickies) {
  set("solutionStickies", stickies);
}

export function getBacklogStickies(fallback) {
  return read("backlogStickies", fallback);
}

export function setBacklogStickies(backlogStickies) {
  set("backlogStickies", backlogStickies);
}

export function getSolutionBacklogStickies(fallback) {
  return read("solution_backlogStickies", fallback);
}

export function setSolutionBacklogStickies(solution_backlogStickies) {
  set("solution_backlogStickies", solution_backlogStickies);
}

export function setBoardStickies(key, stickies) {
  set(key, stickies);
}

export function getSelectedArtIds(fallback) {
  return read("selectedArts", fallback);
}

export function setSelectedArtIds(artIds) {
  set("selectedArts", artIds);
}

export function getSelectedSolutionTrainId() {
  return read("selectedSolutionTrain");
}

export function setSelectedSolutionTrainId(solutionTrainId) {
  set("selectedSolutionTrain", solutionTrainId);
}

export function getIterations() {
  return read("iterations");
}

export function setIterations(iterations) {
  set("iterations", iterations);
}

export function getTeamFieldMapping(fallback) {
  return read("teamFieldMapping", fallback);
}

export function setTeamFieldMapping(teamFieldMapping) {
  set("teamFieldMapping", teamFieldMapping);
}

export function getSessionKanbanBoard(fallback) {
  return read("sessionKanbanBoard", fallback);
}

export function setSessionKanbanBoard(boardId) {
  set("sessionKanbanBoard", boardId);
}

export function getCurrentState() {
  return read("currentState");
}

export function setCurrentState(state) {
  set("currentState", state);
}

export function getStateStack() {
  return read("stateStack");
}

export function setStateStack(stateStack) {
  set("stateStack", stateStack);
}

export function clearState() {
  remove(["currentState", "stateStack"]);
}

export function clearDraft() {
  remove([
    "settings",
    "generalInformation",
    "selectedTeams",
    "selectedArts",
    "selectedSolutionTrain",
    "selectedParticipatingTeams",
    "teamStickies",
    "riskStickies",
    "programStickies",
    "solutionStickies",
    "solution_backlogStickies",
    "backlogStickies",
    "iterations",
    "links",
    "flexBoards",
    "swimlanes",
    "swimlanesConfig",
    "stickers",
    "teamFieldMapping",
    "sessionKanbanBoard",
  ]);
}

export function resetBoards() {
  remove([
    "selectedArts",
    "selectedTeams",
    "teamStickies",
    "riskStickies",
    "programStickies",
    "solutionStickies",
    "solution_backlogStickies",
    "backlogStickies",
  ]);
}

function remove(keys) {
  keys.forEach((item) => storage.removeItem(item));
}

function read(key, fallback) {
  const item = storage.getItem(key);

  if (!item || item === "null" || item === "undefined") {
    return fallback;
  }

  try {
    return JSON.parse(item);
  } catch {
    storage.removeItem(key);
    return fallback;
  }
}

function set(key, value) {
  storage.setItem(key, JSON.stringify(value));
}
